import { defineComponent as _defineComponent } from 'vue'
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'

  import SVGRenderer from '@/components/common/SVGRenderer.vue'
  import VideoOrImageRenderer from '@/components/course/VideoOrImageRenderer.vue'
  import DurationInput from '@/components/common/DurationInput.vue'
  import TextRenderer from '@/components/editor/TextRenderer.vue'
  import EditModuleAuthors from '@/components/editor/EditModuleAuthors.vue'
  import LoadingSpinner from '@/components/common/LoadingSpinner.vue'
  import PlaceholderShimmer from '@/components/common/PlaceholderShimmer.vue'

  import { useRoute, useRouter } from 'vue-router/composables'
  import useCommonMixin from '@/composables/useCommonMixin'
  import useI18n from '@/composables/useI18n'
  import useShopifyClient from '@/composables/useShopifyClient'
  import useIcons from '@/composables/useIcons'
  import useBreakpoint from '@/composables/useBreakpoint'
  import useSegment from '@/composables/useSegment'
  import useEditor from '@/composables/useEditor'
  import useCourse from '@/composables/useCourse'

  import { CourseModule } from '@/store/modules/course'
  import { EditorModule } from '@/store/modules/editor'

  import type {
    ConfigurableModuleOption,
    CourseModule as CourseModuleState,
    Lesson,
  } from '@/services/interfaces/Course'

  import eventBus from '@/main'
  import { VPopover } from 'v-tooltip'

  
export default /*#__PURE__*/_defineComponent({
  __name: 'CourseModule',
  props: {
    trialPeriodExpired: Boolean,
  },
  emits: ['module-action'],
  setup(__props) {

const props = __props;

  

  

  const route = useRoute()
  const router = useRouter()

  const { arrowRight, checkedCircle, checkboxSquareChecked, checkboxSquare, arrowRightIndicator } = useIcons()
  const { course, currentCourseId, moduleId, hasPurchasedAtLeastACourse, courseExpiredAt, moduleActionText } =
    useCourse()
  const { trackPage, pageViewed } = useSegment()
  const { inEditorMode, currentEditorModule } = useEditor(route)
  const { joinArrayOfStringsWithNewlineCharacter, convertToRoman, courseExpiryDuration, formatDate } = useCommonMixin()
  const { isMobileDevice, isDesktopDevice } = useBreakpoint()
  const { translateString } = useI18n()
  const { createShopifyCheckout } = useShopifyClient()

  const fetchingCurrentModule = computed(() => {
    if (inEditorMode.value) return currentEditorModule.value === null
    return currentModule.value === null
  })

  const currentModule = computed(() =>
    inEditorMode.value ? EditorModule.currentModule : CourseModule.currentCourseModule,
  )

  const configurableModuleOptions = computed(
    () =>
      [
        {
          title: translateString('editor.moduleIsFreeTitle'),
          subText: translateString('editor.moduleIsFreeSubtext'),
          action: 'isFree',
          checked: currentEditorModule.value?.isFree,
        },
        {
          title: translateString('editor.showLessonTiles'),
          subText: translateString('editor.showLessonTilesSubtext'),
          action: 'hasTiles',
          checked: currentEditorModule.value?.hasTiles,
        },
        {
          title: translateString('editor.showGuidedQuiz'),
          subText: translateString('editor.showGuidedQuizSubtext'),
          action: 'enableGuidedQuiz',
          checked: currentEditorModule.value?.enableGuidedQuiz,
        },
        {
          title: translateString('editor.moduleIsDisabled'),
          subText: translateString('editor.moduleIsDisabledSubtext'),
          action: 'disabled',
          checked: currentEditorModule.value?.disabled,
        },
      ] as ConfigurableModuleOption[],
  )

  const isPurchasedCourse = computed(() => {
    return CourseModule.course?.purchased
  })

  const moduleIndex = computed(() => {
    const courseModules = CourseModule.courseModules(currentCourseId.value, CourseModule.currentCourseGroupId)
    return courseModules.findIndex((m) => m.id === moduleId.value)
  })

  const currentModuleLearnings = computed(() => {
    if (!currentModule.value?.learnings) return ''
    if (!currentModule.value.learnings.length) return ''
    return joinArrayOfStringsWithNewlineCharacter(currentModule.value.learnings)
  })

  const currentModuleBenefits = computed(() => {
    if (!currentModule.value?.benefits) return ''
    if (!currentModule.value.benefits.length) return ''
    return joinArrayOfStringsWithNewlineCharacter(currentModule.value.benefits)
  })

  const moduleOptionClicked = computed(() => {
    return (action: string) => {
      return configurableModuleOptions.value.find((option) => {
        return option.action === action
      })
    }
  })

  const optionIsChecked = computed(() => {
    return (action: string) => {
      const optionClicked = moduleOptionClicked.value(action)
      return optionClicked?.checked
    }
  })

  const currentEditorModuleAuthors = computed(() => {
    return EditorModule.currentModuleAuthors ?? []
  })

  const courseGroupName = computed(
    () => course.value?.courseGroups.find((cG) => cG.id === currentModule.value?.courseGroupId)?.name,
  )

  const teaserDataEditorProperty = computed(() => {
    if (currentModule.value?.teaserImageUrl || uploadedModuleTeaserImage.value) return 'teaserImageUrl'
    return currentModule.value?.teaserVideoUrl ? 'teaserVideoUrl' : undefined
  })

  const showLessonTiles = computed(
    () =>
      currentModule.value?.hasTiles &&
      !inEditorMode.value &&
      currentModule.value?.lessons &&
      currentModule.value?.lessons.length,
  )

  const showGuidedQuizTile = computed(
    () =>
      currentModule.value?.guidedQuiz &&
      currentModule.value?.guidedQuiz.numberOfQuestions > 0 &&
      currentModule.value.enableGuidedQuiz,
  )

  const isLockedGuidedQuizTile = computed(
    () => (!isPurchasedCourse.value && !course.value?.shopifyProductId) || props.trialPeriodExpired,
  )

  const configureModuleOption = (action: 'isFree' | 'hasTiles' | 'enableGuidedQuiz' | 'disabled') => {
    const optionClicked = moduleOptionClicked.value(action)
    if (optionClicked) {
      optionClicked.checked = !optionClicked.checked
      EditorModule.setEditableState({
        key: 'currentModule' + action,
        path: route.path,
        state: 'currentModule',
        property: action as keyof (Lesson | CourseModuleState),
        value: optionClicked.checked,
      })
    }
  }

  const routeToLessonOrShopifyCheckout = async (lesson: Lesson | null, isGuidedQuiz?: boolean) => {
    if (!currentModule.value?.isFree && !course.value?.purchased && !course.value?.expired) {
      await createShopifyCheckout()
    } else {
      if (isGuidedQuiz) {
        const expired = course.value?.expired || props.trialPeriodExpired
        if (expired) return
        await router.push(`/courses/${course.value?.id}/module/${currentModule.value?.id}/guided-quiz`)
      } else {
        await router.push(`/courses/${course.value?.id}/module/${currentModule.value?.id}/lesson/${lesson!.id}`)
      }
    }
  }

  const onVideoUpload = (url: string) => {
    uploadedModuleTeaserImage.value = false
    EditorModule.setTeaserVideoUrl(url)
  }

  const uploadedModuleTeaserImage = ref(false)
  const onImageUpload = () => {
    uploadedModuleTeaserImage.value = true
  }

  const updateCourseGroup = () => {
    eventBus.$emit('show-modal', {
      modalContentComponent: 'CreateOrUpdateCourseGroupModal',
      modalProps: {
        courseId: currentCourseId.value,
        updateCourseGroup: true,
      },
      cssClass: 'create-course-group-modal',
      modalCloseCallback: (callback: () => void) => {
        if (typeof callback === 'function') callback()
      },
    })
  }

  const hoveringGuidedQuizCTA = ref(false)
  const showExpiredCourseTooltip = ref(false)

  watch(moduleId, () => {
    trackPage('Course Module View')
    pageViewed('Course Module View')
  })

  watch(hoveringGuidedQuizCTA, (value) => {
    showExpiredCourseTooltip.value = value && course.value!.expired
  })

  onMounted(() => {
    trackPage('Course Module View')
    pageViewed('Course Module View')
  })

  onBeforeUnmount(() => {
    if (inEditorMode.value) {
      EditorModule.resetEditorChanges()
    }
  })

return { __sfc: true,props, route, router, arrowRight, checkedCircle, checkboxSquareChecked, checkboxSquare, arrowRightIndicator, course, currentCourseId, moduleId, hasPurchasedAtLeastACourse, courseExpiredAt, moduleActionText, trackPage, pageViewed, inEditorMode, currentEditorModule, joinArrayOfStringsWithNewlineCharacter, convertToRoman, courseExpiryDuration, formatDate, isMobileDevice, isDesktopDevice, translateString, createShopifyCheckout, fetchingCurrentModule, currentModule, configurableModuleOptions, isPurchasedCourse, moduleIndex, currentModuleLearnings, currentModuleBenefits, moduleOptionClicked, optionIsChecked, currentEditorModuleAuthors, courseGroupName, teaserDataEditorProperty, showLessonTiles, showGuidedQuizTile, isLockedGuidedQuizTile, configureModuleOption, routeToLessonOrShopifyCheckout, onVideoUpload, uploadedModuleTeaserImage, onImageUpload, updateCourseGroup, hoveringGuidedQuizCTA, showExpiredCourseTooltip, computed, onBeforeUnmount, onMounted, ref, watch, SVGRenderer, VideoOrImageRenderer, DurationInput, TextRenderer, EditModuleAuthors, LoadingSpinner, PlaceholderShimmer, useRoute, useRouter, useCommonMixin, useI18n, useShopifyClient, useIcons, useBreakpoint, useSegment, useEditor, useCourse, CourseModule, EditorModule, eventBus, VPopover }
}

})